import i18n from "./i18n/config";
import { getCookie } from "./utils";

export interface ScoreEntry {
  player_name: string;
  completion_time_seconds: number;
  found_count: number;
  target_count: number;
  moves: Array<{
    title: string;
    duration_seconds: number;
    is_target: boolean;
  }>;
}

export interface ScoreboardResponse {
  date: string;
  language: string;
  scores: ScoreEntry[];
}

let authFetchingPromise: any = null;
export const api = {
  async ensureAuthenticated(): Promise<void> {
    if (getCookie("id")) return;
    if (authFetchingPromise) return await authFetchingPromise;
    authFetchingPromise = fetch("/api", {
      credentials: "include",
    });
    const response = await authFetchingPromise;
    if (!response.ok) {
      throw new Error(i18n.t("errors.authFailed"));
    }
  },

  async updateName(name: string): Promise<void> {
    // not 100% sure we needed it but it doesnt hurt we will need it in the future anyway
    await this.ensureAuthenticated();
    const params = new URLSearchParams();
    params.append("name", name);

    const response = await fetch("/api/change-name", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
    });

    if (!response.ok) {
      const text = await response.text().catch(() => null);
      throw new Error(text || i18n.t("errors.nameUpdateFailed"));
    }
  },

  async createDailyChallenge(language = "en"): Promise<string> {
    await this.ensureAuthenticated();
    const params = new URLSearchParams();
    params.append("language", language);

    const response = await fetch("/api/daily", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
      credentials: "include", // Important: include cookies for session
      redirect: "manual",
    });

    // Check if we got redirected (not authenticated)
    if (response.status === 0 || response.type === "opaqueredirect") {
      throw new Error(i18n.t("errors.notAuthenticated"));
    }

    // Extract lobby ID from HX-Redirect header or Location header
    const redirectUrl =
      response.headers.get("HX-Redirect") || response.headers.get("Location");
    if (redirectUrl) {
      const match = redirectUrl.match(/\/lobby\/(.+)$/);
      if (match) {
        return match[1];
      }
    }

    throw new Error(i18n.t("errors.noLobbyId"));
  },

  async createLobby(language = "en"): Promise<string> {
    await this.ensureAuthenticated();
    const params = new URLSearchParams();
    params.append("language", language);

    const response = await fetch("/api/lobby/", {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: params.toString(),
      credentials: "include", // Important: include cookies for session
      redirect: "manual",
    });

    // Check if we got redirected (not authenticated)
    if (response.status === 0 || response.type === "opaqueredirect") {
      throw new Error(i18n.t("errors.notAuthenticated"));
    }

    // Extract lobby ID from HX-Redirect header or Location header
    const redirectUrl =
      response.headers.get("HX-Redirect") || response.headers.get("Location");
    if (redirectUrl) {
      const match = redirectUrl.match(/\/lobby\/(.+)$/);
      if (match) {
        return match[1];
      }
    }

    throw new Error(i18n.t("errors.noLobbyId"));
  },

  async getScoreboard(
    language = "en",
    daysAgo = 0,
  ): Promise<ScoreboardResponse> {
    await this.ensureAuthenticated();
    const response = await fetch(
      `/api/daily/scoreboard?language=${language}&days_ago=${daysAgo}`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(i18n.t("errors.scoreboardFailed"));
    }

    return response.json();
  },

  async getStreamer(): Promise<string | null> {
    const response = await fetch("/streamer");

    if (!response.ok) {
      return null;
    }

    const streamer = (await response.text()).trim();
    return streamer || null;
  },
};

// ==================== TESTS ====================

import * as fc from "fast-check";

if (import.meta.vitest) {
  const { describe, it, expect, vi } = import.meta.vitest;

  describe("api", () => {
    it("updateName sends POST request with correct body", async () => {
      const mockFetch = vi.fn().mockResolvedValue({ ok: true });
      vi.stubGlobal("fetch", mockFetch);

      await api.updateName("TestUser");

      expect(mockFetch).toHaveBeenCalledWith(
        "/api/change-name",
        expect.objectContaining({
          method: "POST",
          body: "name=TestUser",
        }),
      );
    });

    it("updateName throws on error response", async () => {
      const mockFetch = vi
        .fn()
        .mockResolvedValue({ ok: false, text: vi.fn().mockResolvedValue("") });
      vi.stubGlobal("fetch", mockFetch);

      await expect(api.updateName("TestUser")).rejects.toThrow(
        "Failed to update name",
      );
    });

    it("fuzz: updateName handles arbitrary name strings", async () => {
      await fc.assert(
        fc.asyncProperty(fc.string(), async (name) => {
          const mockFetch = vi.fn().mockResolvedValue({ ok: true });
          vi.stubGlobal("fetch", mockFetch);

          await expect(api.updateName(name)).resolves.not.toThrow();
        }),
        { numRuns: 100 },
      );
    });
  });
}
